import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HighTensionTraining from "../HighTensionTraining/HighTensionTraining";
import LeaderboardHTTraining from "../LeaderboardHTTraining/LeaderboardHTTraining";
import Processing from "../../Components/Processing/Processing";
import "../TestingSession/TestingSession.css";
import "../DebugSession/DebugSession.css";

// Session shell for the HighTension interactive training. Teacher (not joined)
// sees the live leaderboard; the student (or a joined teacher) runs the
// scenario immediately (no countdown). Mirrors TestingSession.
const HTTrainingSession = ({ data, socket, useStore } : { data: any, socket: any, useStore: any }) => {
    const { sessionId, join } = useParams();
    const joinNumber = useMemo(() => Number(join), []);
    const navigate = useNavigate();
    const location = useLocation();
    const sessionName = location?.state?.sessionName || window.history.state?.sessionName;

    const [initialState, setInitialState] = useState<string>("functiune");
    const [finalState, setFinalState] = useState<string>("ssv");
    const [cell, setCell] = useState<string>("");
    const [loaded, setLoaded] = useState<boolean>(false);
    const [endSessionAnimation, setEndSessionAnimation] = useState<boolean>(false);
    const times = useMemo(() => 0, []);
    const timesRef = useRef(times);

    // Owner = the table the session lives in. A student self-created session is
    // owned by the student (passed via navigation state); otherwise it's the
    // teacher's table.
    const ownerId = location?.state?.ownerId || window.history.state?.ownerId;
    const teacherId = ownerId || (data.userData.type === "teacher" ? data.userData.userId : data.userData.teacherId);

    const getJoinedSession = async () => {
        try {
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/join-session-student-httraining/${teacherId}/${data.userData.userId}/${sessionId}`, { headers: { "Content-Type": "application/json" } });
            const response = await request.json();
            // No countdown: enter the session straight away.
            setEndSessionAnimation(true);
            timesRef.current++;
        } catch (e) { /* ignore */ }
    };

    useLayoutEffect(() => {
        if (timesRef.current > 0) return;
        getJoinedSession().catch((err) => console.log(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/check-id-httraining/${teacherId}/${sessionId}`, { headers: { "Content-Type": "application/json" } });
                const response = await request.json();
                if (!response.success) return navigate(-1);
                // `scenario` column holds "initialState>finalState", or "free".
                const scen = String(response.results?.scenario || "");
                if (scen === "free") {
                    setInitialState("functiune");
                    setFinalState("free");   // sentinel -> free practice (no target/score)
                } else {
                    const [ini, fin] = scen.split(">");
                    setInitialState(ini || "functiune");
                    setFinalState(fin || ini || "ssv");
                }
                setCell(response.results?.cell || "");
                setLoaded(true);
            } catch (e) { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (data.userData.type === "teacher" && !joinNumber) {
        return <LeaderboardHTTraining data={data} socket={socket} sessionName={sessionName} />;
    }

    if (endSessionAnimation && loaded) {
        return (
            <HighTensionTraining
                data={data}
                useStore={useStore}
                session={{
                    teacherId,
                    studentId: data.userData.userId,
                    sessionId: sessionId as string,
                    initialState,
                    finalState,
                    cell,
                    onFinish: () => navigate(`/session-httraining/${sessionId}/leaderboard`, { state: { sessionName, ownerId: teacherId } }),
                }}
            />
        );
    }

    return <Processing />;
};

export default HTTrainingSession;
